import { useMemo } from "react";

import type { ColumnsType } from "@similarweb/pro-logic-components";
import { Button } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";
import SignalsPerHookCell from "app/pages/sales-intelligence/modules/signals-api-webhook/components/SignalsPerHookCell/SignalsPerHookCell";
import SignalTypesCell from "app/pages/sales-intelligence/modules/signals-api-webhook/components/SignalTypesCell/SignalTypesCell";
import URLInputCell from "app/pages/sales-intelligence/modules/signals-api-webhook/components/URLInputCell/URLInputCell";
import { MAX_WEBHOOK_SUBSCRIPTIONS_COUNT } from "app/pages/sales-intelligence/modules/signals-api-webhook/constants/limits";
import type SignalType from "app/pages/sales-intelligence/sub-modules/signals/models/core/SignalType";

import type { IWebhookSubscriptionBaseModel, IWebhookSubscriptionTableModel } from "../types/row-data";
import SubscriptionRemoveButton from "app/pages/sales-intelligence/modules/signals-api-webhook/components/SubscriptionRemoveButton/SubscriptionRemoveButton";

type WebhookHandlerFn = <K extends keyof IWebhookSubscriptionTableModel = keyof IWebhookSubscriptionTableModel>(
    rowIndex: number,
    field: K,
    value: IWebhookSubscriptionTableModel[K],
) => void;

const useCreateColumnConfig = (
    subscriptionsValue: readonly IWebhookSubscriptionTableModel[],
    handleCellChange: WebhookHandlerFn,
    handleTest: (rowValue: IWebhookSubscriptionBaseModel) => void,
    handleDelete: (rowValue: IWebhookSubscriptionTableModel) => void,
    handleError: (error: string | null, index: number, fieldName: keyof IWebhookSubscriptionBaseModel) => void,
): ColumnsType<IWebhookSubscriptionTableModel> => {
    return useMemo((): ColumnsType<IWebhookSubscriptionTableModel> => {
        return [
            {
                dataIndex: "callbackUrl",
                key: "callbackUrl",
                title: translate("si.management.integration.signals-api.section.webhook.url-title", {
                    count: subscriptionsValue.length,
                    limit: MAX_WEBHOOK_SUBSCRIPTIONS_COUNT,
                }),
                className: "callbackUrl-cell",
                render: (value: string, { index }: IWebhookSubscriptionTableModel) => (
                    <URLInputCell
                        name="callbackUrl"
                        value={value}
                        index={index}
                        onChange={handleCellChange}
                        onErrorChange={handleError}
                    />
                ),
            },
            {
                dataIndex: "signalTypes",
                key: "signalTypes",
                title: translate("si.management.integration.signals-api.section.webhook.signal-types-title"),
                className: "signalTypes-cell",
                render: (value: readonly SignalType[], { index }: IWebhookSubscriptionTableModel) => (
                    <SignalTypesCell
                        name="signalTypes"
                        value={value}
                        index={index}
                        onChange={handleCellChange}
                        onErrorChange={handleError}
                    />
                ),
            },
            {
                dataIndex: "signalsPerHook",
                key: "signalsPerHook",
                title: translate("si.management.integration.signals-api.section.webhook.signals-batch-limit-title"),
                className: "signalsPerHook-cell",
                width: 180,
                render: (value: number, { index }: IWebhookSubscriptionTableModel) => (
                    <SignalsPerHookCell
                        value={value}
                        name="signalsPerHook"
                        index={index}
                        onChange={handleCellChange}
                        onErrorChange={handleError}
                    />
                ),
            },
            {
                dataIndex: "id",
                key: "testCell",
                width: 104,
                render: (_, rowValue: IWebhookSubscriptionTableModel) => {
                    return (
                        <Button
                            type="flat"
                            onClick={() => handleTest(rowValue)}
                            dataAutomation="test-endpoint"
                            isDisabled={!!rowValue.errors || !rowValue.callbackUrl}
                        >
                            {translate("si.management.integration.signals-api.section.webhook.test")}
                        </Button>
                    );
                },
            },
            {
                dataIndex: "id",
                key: "deleteCell",
                width: 40,
                className: "delete-cell",
                render: (_, rowValue: IWebhookSubscriptionTableModel) => {
                    return (
                        <SubscriptionRemoveButton
                            isDisabled={subscriptionsValue.length === 1}
                            isSubscription={!!rowValue.id}
                            onRemove={() => handleDelete(rowValue)}
                        />
                    );
                },
            },
        ];
    }, [subscriptionsValue.length, handleCellChange, handleTest, handleDelete, handleError]);
};

export default useCreateColumnConfig;
